import NumberFlow from "@number-flow/react";
import { AnimatePresence, motion, type Variants } from "motion/react";
import * as React from "react";

import { easings } from "../../utils/easings";
import { textVariants } from "../ui/text";

/**
 * Parse a period string into a comparable numeric rank (in days). Used to
 * make the period crossfade direction-aware: increasing period slides up,
 * decreasing period slides down. Returns `null` for unrecognized formats —
 * direction falls back to "up" in that case.
 */
function parsePeriodRank(s: string): number | null {
    const trimmed = s.trim().toLowerCase();
    if (trimmed === "all time" || trimmed === "all") return Infinity;
    const m = /^(\d+(?:\.\d+)?)\s*(d|w|m|y)$/.exec(trimmed);
    if (!m) return null;
    const n = parseFloat(m[1]);
    const unit = m[2];
    const days = unit === "d" ? 1 : unit === "w" ? 7 : unit === "m" ? 30 : 365;
    return n * days;
}

const periodSlideVariants: Variants = {
    enter: (dir: "up" | "down") => ({
        y: dir === "up" ? 8 : -8,
        opacity: 0
    }),
    center: { y: 0, opacity: 1 },
    exit: (dir: "up" | "down") => ({
        y: dir === "up" ? -8 : 8,
        opacity: 0
    })
};

type DeltaBadgeVariant = "small" | "body" | "number";

interface DeltaBadgeProps {
    /** Signed delta. Positive → up + success color; negative → down + error
     *  color. `null` / `undefined` / non-finite → renders nothing. */
    value: number | null | undefined;
    /** Optional period suffix rendered as ` (period)` after the value
     *  (e.g. "30d", "90d", "1y", "all time"). */
    period?: string;
    /** Text variant for the badge content. Defaults to `"small"`. Use
     *  `"number"` when paired with a `number-lg` value (Stats-page style). */
    variant?: DeltaBadgeVariant;
    /** Decimal digits for the numeric value. Defaults to 1. */
    digits?: number;
    /** String rendered before the numeric value. Use for currency badges
     *  (`prefix="$"`). */
    prefix?: string;
    /** String rendered after the numeric value. Defaults to `"%"` so a
     *  bare `<DeltaBadge value={1.2} />` reads as a percentage delta. Pass
     *  `""` to drop the suffix (e.g. when paired with a currency `prefix`). */
    suffix?: string;
    className?: string;
}

const ARROW_SIZE_BY_VARIANT: Record<DeltaBadgeVariant, number> = {
    small: 12,
    body: 14,
    number: 18
};

// Up-arrow path. Down state is the same path rotated 180° via motion, so the
// arrow rotates between states instead of swapping shapes.
const ARROW_UP_PATH =
    "M9.40942 1.79004L8.70142 2.49707L7.99927 3.20996L6.58911 1.79004L1.63892 6.74023L3.05298 8.1543L7.99927 3.20996L12.9446 8.1543L14.3586 6.74023L9.40942 1.79004ZM9.00159 14.1975H7.00159V5.19751H9.00159V14.1975Z";

/**
 * Sign-driven delta badge: arrow + animated value + optional period suffix.
 *
 * Positive sign → success (green) + up arrow. Negative sign → error (red) +
 * down arrow. Color and arrow are inferred from `value` — callers don't pass
 * a direction.
 *
 * Animations:
 *   - Numeric value: digits tween via NumberFlow on every value change.
 *   - Arrow direction flip: rotates 180° clockwise. Rotation is cumulative —
 *     down→up keeps rotating clockwise (180→360) rather than unwinding, so the
 *     motion is always one consistent direction.
 *   - Color flip: `transition-colors` smooths the success↔error swap.
 */
export function DeltaBadge({
    value,
    period,
    variant = "small",
    digits = 1,
    prefix,
    suffix = "%",
    className
}: DeltaBadgeProps): React.ReactElement | null {
    const safeValue = value ?? 0;
    const isUp = safeValue >= 0;
    const arrowSize = ARROW_SIZE_BY_VARIANT[variant];

    // Cumulative clockwise rotation. Starts at 0° (up) or 180° (down); each
    // direction flip adds +180° so the arrow never spins counter-clockwise.
    const [rotation, setRotation] = React.useState<number>(isUp ? 0 : 180);
    const prevIsUpRef = React.useRef<boolean>(isUp);
    React.useEffect(() => {
        if (prevIsUpRef.current !== isUp) {
            setRotation((r) => r + 180);
            prevIsUpRef.current = isUp;
        }
    }, [isUp]);

    // Period transition direction: "up" when the new period is greater than
    // the previous (30d → 90d), "down" when smaller (1y → 30d). Drives both
    // the exit of the leaving period text and the enter of the new one via
    // `custom` on AnimatePresence. Defaults to "up" when ranks aren't
    // comparable so unknown formats still animate cleanly.
    const prevPeriodRef = React.useRef<string | undefined>(period);
    let periodDirection: "up" | "down" = "up";
    if (
        period !== undefined &&
        prevPeriodRef.current !== undefined &&
        prevPeriodRef.current !== period
    ) {
        const prevRank = parsePeriodRank(prevPeriodRef.current);
        const currRank = parsePeriodRank(period);
        if (prevRank !== null && currRank !== null && currRank < prevRank) {
            periodDirection = "down";
        }
    }
    prevPeriodRef.current = period;

    if (value === null || value === undefined || !Number.isFinite(value)) {
        return null;
    }

    return (
        <span
            className={`inline-flex items-center gap-0.5 transition-colors duration-300 ease-[var(--ease-out-quad)] ${
                isUp ? "text-success" : "text-error"
            } ${textVariants({ variant })} ${className ?? ""}`}
        >
            {/* Vertical alignment owned by this wrapper span — motion.svg's
                rotation animation writes to inline style.transform and can
                clash with a Tailwind translate-* class on the same element.
                Mobile bumps the arrow up 1px to compensate for the smaller
                font size of the responsive Text variants (the absolute arrow
                size doesn't shrink with the text, so it visually drifts low
                otherwise). Desktop keeps the original 1px-down nudge that
                aligns with the body cap height. */}
            <span className="inline-flex shrink-0 -translate-y-px md:translate-y-px">
                <motion.svg
                    width={arrowSize}
                    height={arrowSize}
                    viewBox="0 0 16 16"
                    fill="currentColor"
                    aria-hidden="true"
                    initial={{ rotate: rotation }}
                    animate={{ rotate: rotation }}
                    transition={{ duration: 0.45, ease: easings.outQuart }}
                    style={{
                        transformOrigin: "center",
                        transformBox: "fill-box"
                    }}
                >
                    <path d={ARROW_UP_PATH} />
                </motion.svg>
            </span>
            <span className="inline-flex items-center">
                <NumberFlow
                    value={Math.abs(value)}
                    format={{
                        minimumFractionDigits: digits,
                        maximumFractionDigits: digits
                    }}
                    prefix={prefix}
                    suffix={suffix}
                />
                {period ? (
                    <AnimatePresence
                        mode="popLayout"
                        initial={false}
                        custom={periodDirection}
                    >
                        <motion.span
                            key={period}
                            custom={periodDirection}
                            variants={periodSlideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{
                                duration: 0.25,
                                ease: easings.outQuart
                            }}
                            className="inline-block"
                        >
                            &nbsp;({period})
                        </motion.span>
                    </AnimatePresence>
                ) : null}
            </span>
        </span>
    );
}

export type { DeltaBadgeProps, DeltaBadgeVariant };
